/**
 * What a home sale leaves once the adviser has allocated it — and, deliberately, nothing else.
 *
 * WHY THIS IS ITS OWN FUNCTION, AND WHY ITS SIGNATURE IS NARROW (owner-caught, 25 September 2026).
 * The aged care analyser used to work this out inline and then subtract an annuity's purchase price
 * from the answer "so we don't double-count" — while the annuity applier withdrew the same amount
 * from the same account a moment later, and a third deduction followed as a "safety net". A
 * $190,000 annuity cost the household $380,000 of assets.
 *
 * This function is handed the sale allocation and NOTHING about any strategy funded out of the
 * proceeds afterwards. It cannot subtract an annuity because it cannot see one. That is a stronger
 * guarantee than a test: the fault cannot be reintroduced here without changing the signature,
 * which is a far louder change than editing a line.
 *
 * Money spent out of the proceeds after this point — an annuity, a gift, a bond — leaves through
 * the applier that owns it, once. See `applyAnnuityWithDraw` in annuityStrategies.ts.
 */

/** The sale, and the destinations the allocator on screen offers for it. Every field optional: a half-filled form is normal. */
export interface SaleProceedsAllocation {
    salePrice?: number | string | null;
    sellingCosts?: number | string | null;
    /** Duty on the home being BOUGHT, netted off exactly as the allocator on screen does. */
    stampDuty?: number | string | null;
    radAllocation?: number | string | null;
    newHomePurchasePrice?: number | string | null;
    downsizerContribution?: number | string | null;
    partnerDownsizerContribution?: number | string | null;
    giftingAmount?: number | string | null;
    funeralBondPurchase?: number | string | null;
    purchaseInvestmentProperty?: number | string | null;
    purchaseShares?: number | string | null;
}

const num = (v: unknown): number => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
};

/** The sale price less what it costs to sell and the duty on the home being bought. */
export function netSaleProceeds(allocation: SaleProceedsAllocation): number {
    return num(allocation?.salePrice) - num(allocation?.sellingCosts) - num(allocation?.stampDuty);
}

/** Everything the adviser has pointed the proceeds at. */
export function allocatedFromSaleProceeds(allocation: SaleProceedsAllocation): number {
    return num(allocation?.radAllocation)
        + num(allocation?.newHomePurchasePrice)
        + num(allocation?.downsizerContribution)
        + num(allocation?.partnerDownsizerContribution)
        + num(allocation?.giftingAmount)
        + num(allocation?.funeralBondPurchase)
        + num(allocation?.purchaseInvestmentProperty)
        + num(allocation?.purchaseShares);
}

/**
 * What is left over, which sits in the bank as "Unallocated Sale Proceeds" — counted and deemed.
 * May be negative where the adviser has allocated more than the sale raises; the screen says so
 * rather than hiding it, so this does not clamp.
 */
export function unallocatedSaleProceeds(allocation: SaleProceedsAllocation): number {
    return netSaleProceeds(allocation) - allocatedFromSaleProceeds(allocation);
}
